import { useState, useEffect, useMemo } from 'react'

interface D1TableInfo {
  name: string
  rowCount: number
}

export interface D1Filter {
  col: string
  val: string | number
}

interface D1TableData {
  table: string
  columns: string[]
  rows: Record<string, unknown>[]
  totalRows: number
  limit: number
  offset: number
  filter: D1Filter[] | null
}

interface D1RowData {
  table: string
  row: Record<string, unknown>
}

export function useD1Tables(db: string = 'seeds') {
  const [tables, setTables] = useState<D1TableInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchTables() {
      try {
        const response = await fetch(`/api/d1-browser?db=${db}`)
        if (!response.ok) throw new Error('Failed to fetch D1 tables')
        const data = await response.json()
        setTables(data.tables || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchTables()
  }, [db])

  return { tables, loading, error }
}

export interface UseD1TableDataOpts {
  limit?: number
  offset?: number
  db?: string
  /** Single-column equality shortcut. Combined with `filters` if both provided. */
  filterCol?: string
  filterVal?: string | number
  /** Multi-column equality (AND-joined). */
  filters?: D1Filter[]
}

export function useD1TableData(table: string | null, opts: UseD1TableDataOpts = {}) {
  const { limit = 100, offset = 0, db = 'seeds', filterCol, filterVal, filters } = opts
  // Stable key so passing inline filters arrays doesn't cause re-fetch loops.
  const filtersKey = useMemo(() => {
    const all: D1Filter[] = []
    if (filterCol !== undefined && filterVal !== undefined) all.push({ col: filterCol, val: filterVal })
    if (filters) all.push(...filters)
    return all
  }, [filterCol, filterVal, filters])
  const filtersKeyStr = filtersKey.map(f => `${f.col}=${f.val}`).join('&')

  const [data, setData] = useState<D1TableData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!table) {
      setData(null)
      return
    }

    setLoading(true)
    setError(null)

    async function fetchData() {
      try {
        const params = new URLSearchParams({ limit: String(limit), offset: String(offset), db })
        for (const f of filtersKey) {
          params.append('filterCol', f.col)
          params.append('filterVal', String(f.val))
        }
        const response = await fetch(`/api/d1-browser/${table}?${params}`)
        if (!response.ok) throw new Error('Failed to fetch table data')
        const result = await response.json()
        setData(result)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
    // filtersKey identity is stable across renders with same values; filtersKeyStr
    // is the actual content fingerprint for dep-array comparison.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, limit, offset, db, filtersKeyStr])

  return { data, loading, error }
}

export function useD1Row(table: string | null, keyCol: string | null, keyVal: string | null, db: string = 'seeds') {
  const [row, setRow] = useState<D1RowData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!table || !keyCol || !keyVal) {
      setRow(null)
      return
    }

    setLoading(true)
    setError(null)

    async function fetchRow() {
      try {
        const params = new URLSearchParams({ key: keyCol!, value: keyVal!, db })
        const response = await fetch(`/api/d1-browser/${table}/row?${params}`)
        if (!response.ok) throw new Error('Row not found')
        const result = await response.json()
        setRow(result)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchRow()
  }, [table, keyCol, keyVal, db])

  return { row, loading, error }
}
